import { useAppSelector, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { CapabilityKey, useApplicationCapabilitiesAllowed, useApplicationCapabilityReady } from "@next-platform/client-common-core/infra/application/capabilities";
import { Alert, AlertActions, AlertVariant, useMedia } from "@next-platform/client-common-spicy-ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { QueueStatus, ROUTES, USER_SUBSCRIPTION_STATUS } from "@/constants";
import useAuth from "@/services/useAuth";
import { getIsPartner } from "@/utils/getIsPartner";

import { Row } from "../customComponents";

const SECONDS_IN_MINUTE = 60;

export default function WaitingQueueHeader() {
  const navigate = useNavigate();
  const { isMobile } = useMedia();
  const auth = useAuth();
  const { t } = useTranslation();

  const { isAuthenticated, permissions } = auth;
  const { status: subscriptionStatus } = useUserInfo();
  const [isPartner, setIsPartner] = useState(false);
  const isSkipWaitingRoom = permissions?.includes("skip_waiting_room");
  const isDisplayAds = permissions?.includes("display_ads");
  const isUserSubscriptionActive = subscriptionStatus === USER_SUBSCRIPTION_STATUS.ACTIVE;

  const { status, position, time_left } = useAppSelector((state) => state.waitingQueue) || {};
  const [isAlertDismissed, setIsAlertDismissed] = useState(false);
  const isWaiting = Number(position) > 0 && status === QueueStatus.WAITING;
  const isWaitingRoomAllowed = useApplicationCapabilitiesAllowed(CapabilityKey.WaitingRoom);
  const isReady = useApplicationCapabilityReady();

  const shouldHideWaitingQueue =
    isReady &&
    (status === QueueStatus.EXPIRED ||
      !isAuthenticated ||
      isAlertDismissed ||
      isPartner ||
      isSkipWaitingRoom ||
      isUserSubscriptionActive ||
      !isWaitingRoomAllowed);

  useEffect(() => {
    async function fetchPartnerStatus() {
      setIsPartner(getIsPartner());
    }

    void fetchPartnerStatus();
  }, []);

  if (shouldHideWaitingQueue) {
    return null;
  }
  const minutes = Math.floor(Number(time_left) / SECONDS_IN_MINUTE);
  const waitingQueueData = {
    waiting: {
      title: t("user:waitingQueue.waiting.title", { minutes }),
      description: t("user:waitingQueue.waiting.description"),
      variant: AlertVariant.ALERT,
    },
    displayAds: {
      title: t("user:adExperience.alert.title"),
      description: t("user:adExperience.alert.description"),
      variant: AlertVariant.DEFAULT,
    },
    actionTitle: t("user:adExperience.alert.cta.goPremium"),
  };

  let waitingQueue = null;
  if (isWaiting) {
    waitingQueue = waitingQueueData.waiting;
  } else if (isDisplayAds) {
    waitingQueue = waitingQueueData.displayAds;
  }

  if (!waitingQueue) {
    return null;
  }

  const actionHandler = (action: AlertActions) => {
    switch (action) {
      case AlertActions.Dismiss:
        return setIsAlertDismissed(true);
      case AlertActions.CTA:
        if (!isMobile) {
          return navigate(ROUTES.subscribe);
        }
        break;
      case AlertActions.ContainerCTA:
        if (isMobile) {
          return navigate(ROUTES.subscribe);
        }
        break;
      default:
        break;
    }
  };

  return (
    <Row>
      <Alert
        data-testid="WaitingQueueAlert"
        visible={true}
        variant={waitingQueue.variant}
        title={waitingQueue.title}
        description={waitingQueue.description}
        actionTitle={isMobile ? undefined : waitingQueueData.actionTitle}
        actionHandler={actionHandler}
      />
    </Row>
  );
}
